import type { DashboardTravelRequest, IGuest } from '@/lib/models/dashboard';
import { DEFAULT_LANGUAGE, type AppLanguage } from '@/constants/languages';

/**
 * Stato del form del modale richiesta cliente, in creazione e in modifica.
 *
 * Un solo oggetto invece dei quindici useState di prima: il precaricamento in
 * modifica sarebbe altrimenti ingestibile, e la validazione deve poter dire a
 * quale tab appartiene ogni errore.
 *
 * I campi numerici e gli array restano stringhe grezze: la conversione avviene
 * una volta sola in toCreatePayload / toUpdatePayload.
 */
export interface ClientRequestFormState {
  // Cliente
  clientMode: 'new' | 'existing';
  selectedClientId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /**
   * Lingua del cliente: interfaccia ed email. Vale solo alla creazione di un
   * cliente nuovo — su un cliente che esiste gia' non si sovrascrive la sua.
   */
  preferredLanguage: AppLanguage;

  // Assegnazione
  advisorId: string;

  // Viaggio
  destinations: string;
  startDate: string;
  endDate: string;
  totalGuests: string;
  budgetMin: string;
  budgetMax: string;
  /** Misto: id delle categorie predefinite e voci scritte a mano. */
  interests: string[];
  additionalNotes: string;

  // Viaggio — campi facoltativi, raccolti finora solo dal form pubblico
  departurePoint: string;
  needsTransportation: boolean;
  travelDatesFlexible: boolean;
  budgetFlexible: boolean;
  guests: IGuest[];
  /** ISO 3166-1 alpha-2 minuscolo: il `residency` che si manda a RateHawk. */
  residency: string;
}

/** Tetto del budget a persona: oltre e' quasi sempre uno zero di troppo. */
export const BUDGET_CAP = 1_000_000;

/** Sentinella della select advisor: l'agency assegna la richiesta a se stessa. */
export const ADVISOR_SELF = '__self__';

export type ClientRequestTab = 'client' | 'trip' | 'travellers' | 'details' | 'notes' | 'assignment';

/**
 * Chi puo' aprire il modale in modifica, e su quali richieste.
 * Rispecchia i guard della edge function update-client-request: l'admin passa
 * sempre, gli altri ruoli operativi solo finche' la richiesta e' aperta.
 * Il traveler non modifica mai (la edge function lo rifiuta con 403).
 */
export const canEditRequest = (
  status: string,
  userRole: string | null,
): boolean => {
  if (userRole === 'admin') return true;
  if (!userRole || !['travel_designer', 'travel_advisor_member', 'agency'].includes(userRole)) return false;
  return !['completed', 'rejected'].includes(status);
};

/** Campo del form -> tab che lo contiene, per il pallino di errore sulla tab. */
export const FIELD_TAB: Record<string, ClientRequestTab> = {
  firstName: 'client',
  lastName: 'client',
  email: 'client',
  phone: 'client',
  selectedClientId: 'client',
  destinations: 'trip',
  endDate: 'trip',
  budgetMax: 'trip',
  totalGuests: 'trip',
};

export const emptyClientRequestForm = (): ClientRequestFormState => ({
  clientMode: 'new',
  selectedClientId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredLanguage: DEFAULT_LANGUAGE,
  advisorId: ADVISOR_SELF,
  destinations: '',
  startDate: '',
  endDate: '',
  totalGuests: '',
  budgetMin: '',
  budgetMax: '',
  interests: [],
  additionalNotes: '',
  departurePoint: '',
  needsTransportation: false,
  travelDatesFlexible: false,
  budgetFlexible: false,
  guests: [],
  residency: '',
});

/** Le date arrivano come ISO o come timestamp: l'input date vuole yyyy-MM-dd. */
const toDateInput = (value: string | null): string => (value ? value.slice(0, 10) : '');

const toNumberInput = (value: number | null): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * Riga della dashboard -> stato del form. In modifica il cliente e' quello della
 * richiesta: clientMode resta 'new' ma la sezione non mostra il toggle, i campi
 * anagrafici sono direttamente editabili.
 */
export const toFormState = (request: DashboardTravelRequest): ClientRequestFormState => ({
  clientMode: 'new',
  selectedClientId: request.userId || '',
  firstName: request.contact.firstName || '',
  lastName: request.contact.lastName || '',
  email: request.contact.email || '',
  phone: request.contact.phone || '',
  // In modifica non si tocca: il campo non viene mostrato.
  preferredLanguage: DEFAULT_LANGUAGE,
  advisorId: request.assignedAdvisorId || ADVISOR_SELF,
  destinations: (request.destinations || []).join(', '),
  startDate: toDateInput(request.travelStartDate),
  endDate: toDateInput(request.travelEndDate),
  totalGuests: toNumberInput(request.totalGuests || null),
  budgetMin: toNumberInput(request.budgetMin),
  budgetMax: toNumberInput(request.budgetMax),
  interests: request.interests || [],
  additionalNotes: request.additionalNotes || '',
  departurePoint: request.departurePoint || '',
  needsTransportation: request.needsTransportation,
  travelDatesFlexible: request.travelDatesFlexible,
  budgetFlexible: request.budgetFlexible,
  guests: request.guestsData || [],
  residency: request.residency || '',
});

/**
 * Il tab "Viaggiatori" segnala quando contiene qualcosa: eta' per singolo viaggiatore
 * o provenienza. Serve soprattutto alle richieste arrivate dal form pubblico, che
 * portano dati che altrimenti resterebbero dietro una tab mai aperta.
 */
export const hasTravellerData = (state: ClientRequestFormState): boolean =>
  Boolean(state.guests.length || state.residency);

const splitList = (value: string): string[] =>
  value.split(',').map((item) => item.trim()).filter(Boolean);

/** Errori di validazione: campo -> chiave i18n del messaggio. */
export type ClientRequestErrors = Record<string, string>;

export const validateClientRequestForm = (
  state: ClientRequestFormState,
  mode: 'create' | 'edit',
): ClientRequestErrors => {
  const errors: ClientRequestErrors = {};

  if (mode === 'create' && state.clientMode === 'existing') {
    if (!state.selectedClientId) errors.selectedClientId = 'clientRequest.errors.clientRequired';
  } else {
    if (!state.firstName.trim()) errors.firstName = 'clientRequest.errors.firstNameRequired';
    if (!state.lastName.trim()) errors.lastName = 'clientRequest.errors.lastNameRequired';
    if (!state.email.trim()) {
      errors.email = 'clientRequest.errors.emailRequired';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim())) {
      errors.email = 'clientRequest.errors.emailInvalid';
    }
    // Il telefono e' obbligatorio solo alla creazione: le richieste arrivate dal
    // form pubblico possono averlo vuoto e non vogliamo bloccarne la modifica.
    if (mode === 'create' && !state.phone.trim()) {
      errors.phone = 'clientRequest.errors.phoneRequired';
    }
  }

  if (!state.destinations.trim()) {
    errors.destinations = 'clientRequest.errors.destinationsRequired';
  }

  if (state.startDate && state.endDate && new Date(state.startDate) >= new Date(state.endDate)) {
    errors.endDate = 'clientRequest.errors.endDateBeforeStart';
  }

  if (state.budgetMin && state.budgetMax && Number(state.budgetMin) > Number(state.budgetMax)) {
    errors.budgetMax = 'clientRequest.errors.budgetMinOverMax';
  } else if (Number(state.budgetMax) > BUDGET_CAP || Number(state.budgetMin) > BUDGET_CAP) {
    errors.budgetMax = 'clientRequest.errors.budgetOverCap';
  }

  if (state.totalGuests && Number(state.totalGuests) < 1) {
    errors.totalGuests = 'clientRequest.errors.guestsInvalid';
  }

  return errors;
};

/** Parte "travelRequest" del body, condivisa da create e update. */
const toTripPayload = (state: ClientRequestFormState) => ({
  destinations: splitList(state.destinations),
  startDate: state.startDate || undefined,
  endDate: state.endDate || undefined,
  budgetMin: state.budgetMin ? Number(state.budgetMin) : undefined,
  budgetMax: state.budgetMax ? Number(state.budgetMax) : undefined,
  totalGuests: state.totalGuests ? Number(state.totalGuests) : undefined,
  interests: state.interests,
  additionalNotes: state.additionalNotes || undefined,
  departurePoint: state.departurePoint || undefined,
  needsTransportation: state.needsTransportation,
  travelDatesFlexible: state.travelDatesFlexible,
  budgetFlexible: state.budgetFlexible,
  guestsData: state.guests.length ? state.guests : undefined,
  residency: state.residency || null,
});

export const toCreatePayload = (
  state: ClientRequestFormState,
  isAgency: boolean,
  existingClient?: { firstName: string; lastName: string; email: string; phone: string | null },
) => ({
  client: state.clientMode === 'existing'
    ? {
        existingClientId: state.selectedClientId,
        firstName: existingClient?.firstName || '',
        lastName: existingClient?.lastName || '',
        email: existingClient?.email || '',
        phone: existingClient?.phone || '',
      }
    : {
        firstName: state.firstName.trim(),
        lastName: state.lastName.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        preferredLanguage: state.preferredLanguage,
      },
  travelRequest: toTripPayload(state),
  ...(isAgency && state.advisorId && state.advisorId !== ADVISOR_SELF
    ? { assignedDesignerId: state.advisorId }
    : {}),
});

export const toUpdatePayload = (
  state: ClientRequestFormState,
  requestId: string,
  canReassign: boolean,
  selfId?: string,
) => ({
  requestId,
  client: {
    firstName: state.firstName.trim(),
    lastName: state.lastName.trim(),
    email: state.email.trim(),
    phone: state.phone.trim(),
  },
  travelRequest: toTripPayload(state),
  ...(canReassign && state.advisorId
    ? { assignedDesignerId: state.advisorId === ADVISOR_SELF ? selfId : state.advisorId }
    : {}),
});
